import { gamma } from "@/lib/tool-kit/arithmetic/factorial";
import { toSuperscript } from "@/lib/tool-kit/math/primes";

/**
 * A scientific calculator's expression engine: tokeniser, recursive-descent parser and
 * evaluator. There is no `eval` and no `Function` anywhere — the text is turned into a tree
 * of numbers and named operations, and only those named operations can run.
 *
 *     expression := term (("+" | "−") term)*
 *     term       := unary (("×" | "÷") unary | implicit unary)*
 *     unary      := ("+" | "−") unary | power
 *     power      := postfix ("^" unary)?          right-associative: 2^3^2 = 2^9
 *     postfix    := primary ("!" | "%" | "²" | "³")*
 *     primary    := number | constant | name "(" arguments ")" | "(" expression ")" | "√" primary
 *
 * So −2^2 is −(2²) = −4 and 2^−1 is ½. An unclosed bracket at the end is closed for you,
 * the way a calculator does. Angles for trigonometric functions are degrees, radians or
 * gradians, and the exact values at multiples of a right angle are returned exactly
 * (sin 180° = 0, not 1.2e-16).
 */
export type AngleMode = "deg" | "rad" | "grad";

export type CalcErrorCode =
  | "empty"
  | "too-long"
  | "too-deep"
  | "bad-character"
  | "unexpected-token"
  | "unexpected-end"
  | "unknown-name"
  | "wrong-arguments"
  | "divide-by-zero"
  | "domain"
  | "undefined-tangent"
  | "overflow";

export interface CalcError {
  code: CalcErrorCode;
  token: string;
}

export type CalcResult =
  | { ok: true; value: number }
  | { ok: false; error: CalcError };

export interface CalcOptions {
  angle: AngleMode;
  /** The last answer, for `ans`. */
  ans: number;
  /** Implicit multiplication binds tighter than ÷, as on many scientific calculators. */
  implicitTight: boolean;
}

export const MAX_LENGTH = 500;
const MAX_DEPTH = 60;
const MAX_FACTORIAL = 170;

/* ------------------------------------------------------------ tokeniser */

type Token =
  | { type: "num"; value: number }
  | { type: "name"; text: string }
  | { type: "op"; text: string }
  | { type: "lparen" }
  | { type: "rparen" }
  | { type: "comma" };

const NUMBER = /(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?/y;
const NAME = /[A-Za-zπτ][A-Za-z0-9]*/y;
const SINGLES: Readonly<Record<string, string>> = {
  "+": "+",
  "-": "-",
  "−": "-",
  "–": "-",
  "*": "*",
  "×": "*",
  "·": "*",
  "⋅": "*",
  "/": "/",
  "÷": "/",
  "^": "^",
  "!": "!",
  "%": "%",
  "²": "²",
  "³": "³",
  "√": "√",
};
const DOUBLE_STAR = /\*\*/g;
const SPACE = /\s/;

function tokenize(input: string): Token[] | CalcError {
  const text = input.replace(DOUBLE_STAR, "^");
  const tokens: Token[] = [];
  let at = 0;
  while (at < text.length) {
    const ch = text[at];
    if (SPACE.test(ch)) {
      at += 1;
      continue;
    }
    NUMBER.lastIndex = at;
    const number = NUMBER.exec(text);
    if (number && number.index === at) {
      tokens.push({ type: "num", value: Number(number[0]) });
      at += number[0].length;
      continue;
    }
    NAME.lastIndex = at;
    const name = NAME.exec(text);
    if (name && name.index === at) {
      tokens.push({ type: "name", text: name[0].toLowerCase() });
      at += name[0].length;
      continue;
    }
    const symbol = punctuation(ch);
    if (symbol === null) {
      return { code: "bad-character", token: ch };
    }
    tokens.push(symbol);
    at += 1;
  }
  return tokens;
}

function punctuation(ch: string): Token | null {
  if (ch === "(" || ch === "[") {
    return { type: "lparen" };
  }
  if (ch === ")" || ch === "]") {
    return { type: "rparen" };
  }
  if (ch === ",") {
    return { type: "comma" };
  }
  const op = SINGLES[ch];
  return op ? { type: "op", text: op } : null;
}

/* --------------------------------------------------------------- the tree */

type Node =
  | { k: "num"; v: number }
  | { k: "const"; name: string }
  | { k: "neg"; x: Node }
  | { k: "bin"; op: "+" | "-" | "*" | "/" | "^"; l: Node; r: Node }
  | { k: "post"; op: "!" | "%" | "²" | "³"; x: Node }
  | { k: "call"; fn: string; args: Node[] };

class Failure extends Error {
  readonly detail: CalcError;
  constructor(code: CalcErrorCode, token = "") {
    super(code);
    this.detail = { code, token };
  }
}

const CONSTANTS: ReadonlySet<string> = new Set([
  "pi",
  "π",
  "e",
  "tau",
  "τ",
  "ans",
]);

const ARITY: Readonly<Record<string, readonly [number, number]>> = {
  sin: [1, 1],
  cos: [1, 1],
  tan: [1, 1],
  asin: [1, 1],
  acos: [1, 1],
  atan: [1, 1],
  arcsin: [1, 1],
  arccos: [1, 1],
  arctan: [1, 1],
  sinh: [1, 1],
  cosh: [1, 1],
  tanh: [1, 1],
  asinh: [1, 1],
  acosh: [1, 1],
  atanh: [1, 1],
  ln: [1, 1],
  log: [1, 2],
  log10: [1, 1],
  log2: [1, 1],
  exp: [1, 1],
  sqrt: [1, 1],
  cbrt: [1, 1],
  root: [2, 2],
  abs: [1, 1],
  floor: [1, 1],
  ceil: [1, 1],
  round: [1, 1],
  sign: [1, 1],
  mod: [2, 2],
  ncr: [2, 2],
  npr: [2, 2],
  deg: [1, 1],
  rad: [1, 1],
};

/** A Map, not an object lookup: "constructor" and "__proto__" must not be names. */
const ARITY_MAP: ReadonlyMap<string, readonly [number, number]> = new Map(
  Object.entries(ARITY)
);

class Parser {
  private index = 0;
  private depth = 0;
  private readonly tokens: Token[];
  private readonly implicitTight: boolean;

  constructor(tokens: Token[], implicitTight: boolean) {
    this.tokens = tokens;
    this.implicitTight = implicitTight;
  }

  parseAll(): Node {
    const node = this.expression();
    const extra = this.tokens[this.index];
    if (extra) {
      throw new Failure("unexpected-token", describe(extra));
    }
    return node;
  }

  private peek(): Token | undefined {
    return this.tokens[this.index];
  }

  private enter() {
    this.depth += 1;
    if (this.depth > MAX_DEPTH) {
      throw new Failure("too-deep");
    }
  }

  private isOp(token: Token | undefined, ...ops: string[]): boolean {
    return token?.type === "op" && ops.includes(token.text);
  }

  private expression(): Node {
    this.enter();
    let node = this.term();
    while (this.isOp(this.peek(), "+", "-")) {
      const op = (this.peek() as { text: "+" | "-" }).text;
      this.index += 1;
      node = { k: "bin", op, l: node, r: this.term() };
    }
    this.depth -= 1;
    return node;
  }

  /** A token that can begin a primary, so a product may be written without a sign. */
  private startsOperand(token: Token | undefined): boolean {
    return (
      token?.type === "name" ||
      token?.type === "lparen" ||
      (token?.type === "op" && token.text === "√")
    );
  }

  private term(): Node {
    let node = this.factor();
    for (;;) {
      const token = this.peek();
      if (this.isOp(token, "*", "/")) {
        this.index += 1;
        const op = (token as { text: "*" | "/" }).text;
        node = { k: "bin", op, l: node, r: this.factor() };
      } else if (!this.implicitTight && this.startsOperand(token)) {
        node = { k: "bin", op: "*", l: node, r: this.factor() };
      } else {
        return node;
      }
    }
  }

  /** With tight implicit multiplication, juxtaposed operands multiply before ÷ does. */
  private factor(): Node {
    let node = this.unary();
    while (this.implicitTight && this.startsOperand(this.peek())) {
      node = { k: "bin", op: "*", l: node, r: this.unary() };
    }
    return node;
  }

  private unary(): Node {
    const token = this.peek();
    if (this.isOp(token, "+")) {
      this.index += 1;
      return this.unary();
    }
    if (this.isOp(token, "-")) {
      this.index += 1;
      this.enter();
      const x = this.unary();
      this.depth -= 1;
      return { k: "neg", x };
    }
    return this.power();
  }

  private power(): Node {
    const base = this.postfix();
    if (this.isOp(this.peek(), "^")) {
      this.index += 1;
      this.enter();
      const exponent = this.unary();
      this.depth -= 1;
      return { k: "bin", op: "^", l: base, r: exponent };
    }
    return base;
  }

  private postfix(): Node {
    let node = this.primary();
    for (;;) {
      const token = this.peek();
      if (token?.type === "op" && ["!", "%", "²", "³"].includes(token.text)) {
        this.index += 1;
        node = { k: "post", op: token.text as "!" | "%" | "²" | "³", x: node };
      } else {
        return node;
      }
    }
  }

  private primary(): Node {
    const token = this.tokens[this.index];
    this.index += 1;
    if (!token) {
      throw new Failure("unexpected-end");
    }
    if (token.type === "num") {
      return { k: "num", v: token.value };
    }
    if (token.type === "name") {
      return this.named(token.text);
    }
    if (token.type === "lparen") {
      this.enter();
      const inner = this.expression();
      this.depth -= 1;
      this.closeParen();
      return inner;
    }
    if (token.type === "op" && token.text === "√") {
      this.enter();
      const x = this.unary();
      this.depth -= 1;
      return { k: "call", fn: "sqrt", args: [x] };
    }
    throw new Failure("unexpected-token", describe(token));
  }

  /** A missing closing bracket at the very end is supplied; anywhere else it is an error. */
  private closeParen() {
    const next = this.tokens[this.index];
    if (next?.type === "rparen") {
      this.index += 1;
    } else if (next !== undefined) {
      throw new Failure("unexpected-token", describe(next));
    }
  }

  private named(text: string): Node {
    if (CONSTANTS.has(text)) {
      return { k: "const", name: text };
    }
    const arity = ARITY_MAP.get(text);
    if (!arity) {
      throw new Failure("unknown-name", text);
    }
    if (this.peek()?.type !== "lparen") {
      throw new Failure("unexpected-end", text);
    }
    this.index += 1;
    this.enter();
    const args: Node[] = [this.expression()];
    while (this.peek()?.type === "comma") {
      this.index += 1;
      args.push(this.expression());
    }
    this.depth -= 1;
    this.closeParen();
    if (args.length < arity[0] || args.length > arity[1]) {
      throw new Failure("wrong-arguments", text);
    }
    return { k: "call", fn: text, args };
  }
}

function describe(token: Token): string {
  if (token.type === "num") {
    return String(token.value);
  }
  if (token.type === "name" || token.type === "op") {
    return token.text;
  }
  if (token.type === "lparen") {
    return "(";
  }
  return token.type === "rparen" ? ")" : ",";
}

/* ----------------------------------------------------------- evaluation */

const DEGREES_TO_RADIANS = Math.PI / 180;
const GRADIANS_TO_RADIANS = Math.PI / 200;

function toRadians(angle: number, mode: AngleMode): number {
  if (mode === "deg") {
    return angle * DEGREES_TO_RADIANS;
  }
  return mode === "grad" ? angle * GRADIANS_TO_RADIANS : angle;
}

function fromRadians(angle: number, mode: AngleMode): number {
  if (mode === "deg") {
    return angle / DEGREES_TO_RADIANS;
  }
  return mode === "grad" ? angle / GRADIANS_TO_RADIANS : angle;
}

const QUARTER: Readonly<Record<AngleMode, number>> = {
  deg: 90,
  rad: 0,
  grad: 100,
};

/** Trig of an exact multiple of a right angle, where the answer is 0 or ±1. */
function quarterTurn(
  fn: "sin" | "cos" | "tan",
  angle: number,
  mode: AngleMode
): number | null {
  const quarter = QUARTER[mode];
  if (quarter === 0 || angle % quarter !== 0) {
    return null;
  }
  const turn = (((angle / quarter) % 4) + 4) % 4;
  const sin = [0, 1, 0, -1][turn];
  const cos = [1, 0, -1, 0][turn];
  if (fn === "sin") {
    return sin;
  }
  if (fn === "cos") {
    return cos;
  }
  if (cos === 0) {
    throw new Failure("undefined-tangent");
  }
  return sin / cos;
}

/** A value within 10⁻¹⁵ of zero that a trig function produced from a sizeable angle is rounding noise. */
function snapTiny(result: number, radians: number): number {
  return Math.abs(result) < 1e-15 && Math.abs(radians) > 1e-8 ? 0 : result;
}

function trig(fn: "sin" | "cos" | "tan", x: number, mode: AngleMode): number {
  const exact = quarterTurn(fn, x, mode);
  if (exact !== null) {
    return exact;
  }
  const radians = toRadians(x, mode);
  const value = Math[fn](radians);
  return snapTiny(value, radians);
}

function factorialOf(x: number): number {
  if (Number.isInteger(x)) {
    if (x < 0) {
      throw new Failure("domain", "!");
    }
    if (x > MAX_FACTORIAL) {
      throw new Failure("overflow", "!");
    }
    let result = 1;
    for (let k = 2; k <= x; k++) {
      result *= k;
    }
    return result;
  }
  if (x < -MAX_FACTORIAL || x > MAX_FACTORIAL) {
    throw new Failure("overflow", "!");
  }
  return gamma(x + 1);
}

function choose(n: number, k: number, ordered: boolean): number {
  if (
    !(Number.isInteger(n) && Number.isInteger(k)) ||
    n < 0 ||
    k < 0 ||
    k > n
  ) {
    throw new Failure("domain", ordered ? "nPr" : "nCr");
  }
  let result = 1;
  for (let i = 1; i <= k; i++) {
    result = ordered ? result * (n - k + i) : (result * (n - k + i)) / i;
  }
  return Math.round(result);
}

function need(value: number, code: CalcErrorCode, token: string): number {
  if (Number.isNaN(value)) {
    throw new Failure("domain", token);
  }
  if (!Number.isFinite(value)) {
    throw new Failure(code, token);
  }
  return value;
}

function rootOf(x: number, n: number): number {
  if (!Number.isInteger(n) || n === 0) {
    throw new Failure("domain", "root");
  }
  if (x < 0) {
    if (n % 2 === 0) {
      throw new Failure("domain", "root");
    }
    return -((-x) ** (1 / n));
  }
  return x ** (1 / n);
}

function logBase(x: number, base: number): number {
  if (x <= 0 || base <= 0 || base === 1) {
    throw new Failure("domain", "log");
  }
  return Math.log(x) / Math.log(base);
}

type Impl = (a: number, b: number, mode: AngleMode) => number;

const FUNCTIONS: Readonly<Record<string, Impl>> = {
  sin: (a, _b, m) => trig("sin", a, m),
  cos: (a, _b, m) => trig("cos", a, m),
  tan: (a, _b, m) => trig("tan", a, m),
  asin: (a, _b, m) => inverse(Math.asin, a, m, "asin"),
  arcsin: (a, _b, m) => inverse(Math.asin, a, m, "asin"),
  acos: (a, _b, m) => inverse(Math.acos, a, m, "acos"),
  arccos: (a, _b, m) => inverse(Math.acos, a, m, "acos"),
  atan: (a, _b, m) => fromRadians(Math.atan(a), m),
  arctan: (a, _b, m) => fromRadians(Math.atan(a), m),
  sinh: (a) => Math.sinh(a),
  cosh: (a) => Math.cosh(a),
  tanh: (a) => Math.tanh(a),
  asinh: (a) => Math.asinh(a),
  acosh: (a) => need(Math.acosh(a), "domain", "acosh"),
  atanh: (a) =>
    Math.abs(a) >= 1 ? need(Number.NaN, "domain", "atanh") : Math.atanh(a),
  ln: (a) => (a > 0 ? Math.log(a) : need(Number.NaN, "domain", "ln")),
  log: (a, b) => (Number.isNaN(b) ? logBase(a, 10) : logBase(a, b)),
  log10: (a) => logBase(a, 10),
  log2: (a) => logBase(a, 2),
  exp: (a) => Math.exp(a),
  sqrt: (a) => (a >= 0 ? Math.sqrt(a) : need(Number.NaN, "domain", "√")),
  cbrt: (a) => Math.cbrt(a),
  root: (a, b) => rootOf(a, b),
  abs: (a) => Math.abs(a),
  floor: (a) => Math.floor(a),
  ceil: (a) => Math.ceil(a),
  round: (a) => Math.sign(a) * Math.round(Math.abs(a)),
  sign: (a) => Math.sign(a),
  mod: (a, b) => {
    if (b === 0) {
      throw new Failure("divide-by-zero", "mod");
    }
    return a - b * Math.floor(a / b);
  },
  ncr: (a, b) => choose(a, b, false),
  npr: (a, b) => choose(a, b, true),
  deg: (a) => (a * 180) / Math.PI,
  rad: (a) => (a * Math.PI) / 180,
};

const FUNCTION_MAP: ReadonlyMap<string, Impl> = new Map(
  Object.entries(FUNCTIONS)
);

/** Inverse sine and cosine, whose results are angles in the chosen unit. */
function inverse(
  fn: (x: number) => number,
  x: number,
  mode: AngleMode,
  token: string
): number {
  if (x < -1 || x > 1) {
    throw new Failure("domain", token);
  }
  return fromRadians(fn(x), mode);
}

function constantValue(name: string, options: CalcOptions): number {
  switch (name) {
    case "pi":
    case "π":
      return Math.PI;
    case "tau":
    case "τ":
      return 2 * Math.PI;
    case "e":
      return Math.E;
    default:
      return options.ans;
  }
}

function binary(op: "+" | "-" | "*" | "/" | "^", l: number, r: number): number {
  switch (op) {
    case "+":
      return l + r;
    case "-":
      return l - r;
    case "*":
      return l * r;
    case "/":
      if (r === 0) {
        throw new Failure("divide-by-zero", "÷");
      }
      return l / r;
    default:
      if (l === 0 && r < 0) {
        throw new Failure("divide-by-zero", "^");
      }
      return need(l ** r, "overflow", "^");
  }
}

function evaluateNode(node: Node, options: CalcOptions): number {
  switch (node.k) {
    case "num":
      return node.v;
    case "const":
      return constantValue(node.name, options);
    case "neg":
      return -evaluateNode(node.x, options);
    case "bin":
      return need(
        binary(
          node.op,
          evaluateNode(node.l, options),
          evaluateNode(node.r, options)
        ),
        "overflow",
        node.op
      );
    case "post": {
      const x = evaluateNode(node.x, options);
      if (node.op === "!") {
        return factorialOf(x);
      }
      if (node.op === "%") {
        return x / 100;
      }
      return need(node.op === "²" ? x * x : x * x * x, "overflow", node.op);
    }
    default: {
      const args = node.args.map((a) => evaluateNode(a, options));
      const impl = FUNCTION_MAP.get(node.fn);
      if (!impl) {
        throw new Failure("unknown-name", node.fn);
      }
      return need(
        impl(args[0], args[1] ?? Number.NaN, options.angle),
        "overflow",
        node.fn
      );
    }
  }
}

/** Evaluate an expression. Never throws: every problem comes back as an error code. */
export function evaluateExpression(
  input: string,
  options: CalcOptions
): CalcResult {
  const text = input.trim();
  if (text === "") {
    return { ok: false, error: { code: "empty", token: "" } };
  }
  if (text.length > MAX_LENGTH) {
    return { ok: false, error: { code: "too-long", token: "" } };
  }
  const tokens = tokenize(text);
  if (!Array.isArray(tokens)) {
    return { ok: false, error: tokens };
  }
  try {
    const tree = new Parser(tokens, options.implicitTight).parseAll();
    const value = evaluateNode(tree, options);
    if (!Number.isFinite(value)) {
      return { ok: false, error: { code: "overflow", token: "" } };
    }
    return { ok: true, value: Object.is(value, -0) ? 0 : value };
  } catch (error) {
    if (error instanceof Failure) {
      return { ok: false, error: error.detail };
    }
    throw error;
  }
}

/* -------------------------------------------------------------- display */

export type DisplayMode = "normal" | "scientific";

const NORMAL_LOW = 1e-6;
const NORMAL_HIGH = 1e12;

function trimMantissa(text: string): string {
  return text.includes(".")
    ? text.replace(TRAILING_ZEROS, "").replace(TRAILING_POINT, "")
    : text;
}

const TRAILING_ZEROS = /0+$/;
const TRAILING_POINT = /\.$/;

export interface DisplayNumber {
  /** Plain ASCII, with "." — what a copy puts on the clipboard. */
  plain: string;
  /** Readable form with "×10ⁿ" for large and small values. */
  pretty: string;
}

/** A calculator-style rendering: 12 significant digits, no floating-point noise. */
export function displayNumber(value: number, mode: DisplayMode): DisplayNumber {
  if (value === 0) {
    return { plain: "0", pretty: "0" };
  }
  const magnitude = Math.abs(value);
  if (mode === "normal" && magnitude >= NORMAL_LOW && magnitude < NORMAL_HIGH) {
    const text = String(Number(value.toPrecision(12)));
    return { plain: text, pretty: text.replace("-", "−") };
  }
  const [mantissa, exponentText] = value.toExponential(11).split("e");
  const exponent = Number(exponentText);
  const trimmed = trimMantissa(mantissa);
  return {
    plain: `${trimmed}e${exponent}`,
    pretty: `${trimmed.replace("-", "−")} × 10${toSuperscript(exponent)}`,
  };
}

/** The value as text the parser reads back, to full double precision. */
export function machineText(value: number): string {
  return String(Number(value.toPrecision(15)));
}

/** Append the closing brackets a calculator would supply for an unfinished expression. */
export function closeBrackets(expression: string): string {
  let open = 0;
  for (const ch of expression) {
    if (ch === "(" || ch === "[") {
      open += 1;
    } else if ((ch === ")" || ch === "]") && open > 0) {
      open -= 1;
    }
  }
  return expression + ")".repeat(open);
}
